/**
 * v145.27 — CartCheckoutModal
 * v145.28 — Address book prefill via /me/last-checkout
 * v145.29 — Coin panel wired correctly, address field always rendered
 *           when required, mobile scroll-lock, order confirmation with
 *           copy / download bill.
 * v145.31 — Portalled to <body> so it escapes any parent stacking
 *           context (Leaflet maps + block containers with transform
 *           broke position:fixed). Sticky Pay button on mobile so it's
 *           always tap-reachable no matter how many cart items or how
 *           tall the form gets.
 * v149.36 — Cash on Delivery. COD existed only on the single-product
 *           checkouts, so a buyer with two things in the basket was
 *           shown Razorpay and nothing else however the seller's plan
 *           and settings were configured. Same toggle, same wording and
 *           the same pending-confirmation contract as the single-product
 *           flow; the seller confirms each line from Orders.
 * v149.40 — Seller mode. A brand ringing up an order from Orders → New
 *           order checks out on this same screen rather than a second
 *           checkout. Pass `seller` and the lines become editable
 *           (quantity, selling price, remove), the order takes a
 *           priority and a hand-over choice, and "Place order" goes
 *           through `seller.place()` — the brand's manual-order endpoint,
 *           paid in person, no gateway. Coupons and coins are the
 *           buyer's own and stay customer-only. Without `seller`, nothing
 *           here behaves differently.
 * v149.63 — Seller mode takes the customer's loyalty coins, by the counter
 *           rules (New Bill → Total bill's): the customer's phone comes
 *           first, `SellerCoinPanel` finds their balance as it's typed and
 *           the coins come off the total; the bill earns them coins too.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Loader2, ShoppingBag, Ticket, X, CheckCircle2, Copy, Download, Minus, Plus, Trash2,
} from "lucide-react";
import PhoneInput from "../PhoneInput";
import { LoyaltyCoinPanel } from "./LoyaltyCoinPanel";
import { toast } from "sonner";
import { cleanText, cleanPrefill } from "../../lib/razorpayOptions";
import useCreatorLoyalty from "../../hooks/useCreatorLoyalty";
import CoinEarnChip from "../CoinEarnChip";
import { PriorityPicker } from "./orderPriority";
import SellerCoinPanel, { coinDiscount as sellerCoinDiscount } from "./SellerCoinPanel";

// v149.71 — Send Bill + Print on the shop's own "Order placed" screen.
// Loaded only then: this modal is also the customers' checkout, which is
// on the public pages' bundle and never shows them.
const BillActions = lazy(() => import("./BillActions"));

const API = process.env.REACT_APP_BACKEND_URL;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CartCheckoutModal({
  slug,
  cartList,
  buyerUser,
  requirePhone = false,
  requireAddress = false,
  deliveryCharge = 0,
  // v149.36 — Whether this seller takes Cash on Delivery. Sourced from
  // `/public/storefront/{slug}.settings.cod_enabled`, which the backend
  // only reports true when the plan allows it AND the seller has turned
  // it on — so the toggle and the order endpoint always agree.
  codEnabled = false,
  // v149.70 — False for a "Cash on Delivery only" shop
  // (`settings.online_enabled`): COD is then the method, with no choice.
  onlineEnabled = true,
  // v149.40 — { priority, onPriority, onUnits, onPrice, onRemove, place }.
  // Present only for the brand's own New order checkout.
  seller = null,
  onClose,
  onSuccess,
}) {
  const isSeller = !!seller;
  const [fulfilment, setFulfilment] = useState("now"); // seller: now | later
  const [email, setEmail] = useState(buyerUser?.email || "");
  const [name, setName] = useState(buyerUser?.name || "");
  const [phone, setPhone] = useState(buyerUser?.phone || "");
  const [address, setAddress] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponMsg, setCouponMsg] = useState("");
  const [applied, setApplied] = useState(null);
  const [coinsToApply, setCoinsToApply] = useState(0);
  const [coinPreview, setCoinPreview] = useState(null);
  // v149.63 — Seller mode: the counter's coin lookup for this customer.
  const [sellerQuote, setSellerQuote] = useState(null);
  const [step, setStep] = useState("form"); // form | processing | done
  const codOnly = codEnabled && !onlineEnabled && !seller;
  const [paymentMethod, setPaymentMethod] = useState(codOnly ? "cod" : "razorpay"); // razorpay | cod
  useEffect(() => { if (codOnly) setPaymentMethod("cod"); }, [codOnly]);
  const [codOrder, setCodOrder] = useState(null); // { order_id, amount_inr } on a placed COD cart
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null); // paid cart doc after verify
  const authRef = useRef(false);
  const addressPrefillRef = useRef(false);

  // v145.29 — Lock body scroll while modal is open so the underlying
  // page doesn't jitter on mobile Safari when the user drags inside
  // the modal.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!buyerUser || authRef.current) return;
    if (buyerUser.email) setEmail((v) => v || buyerUser.email);
    if (buyerUser.name) setName((v) => v || buyerUser.name);
    if (buyerUser.phone) setPhone((v) => v || buyerUser.phone);
    authRef.current = true;
  }, [buyerUser?.user_id]);

  // Address Book prefill.
  useEffect(() => {
    if (!buyerUser?.user_id || addressPrefillRef.current) return;
    addressPrefillRef.current = true;
    (async () => {
      try {
        const token = typeof localStorage !== "undefined"
          ? localStorage.getItem("creatoraiq_session_token") : null;
        const r = await fetch(`${API}/api/me/last-checkout`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const d = await r.json();
        if (d.name) setName((v) => v || d.name);
        if (d.phone) setPhone((v) => v || d.phone);
        if (d.address) setAddress((v) => v || d.address);
      } catch { /* silent */ }
    })();
  }, [buyerUser?.user_id]);

  const subtotal = useMemo(
    () => cartList.reduce((s, i) => s + Number(i.product?.price || 0) * i.units, 0),
    [cartList],
  );
  const items = useMemo(
    () => cartList.map((i) => ({ product_id: i.product.product_id, units: i.units })),
    [cartList],
  );

  const applyCoupon = async () => {
    setCouponMsg("");
    if (!couponCode.trim()) return;
    try {
      const r = await fetch(`${API}/api/public/storefront/cart/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ creator_slug: slug, items, coupon_code: couponCode.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Could not apply coupon");
      if (!d.coupon) { setApplied(null); setCouponMsg("Coupon not valid on this cart"); return; }
      setApplied(d.coupon);
      setCouponMsg(`Applied — you save ₹${d.coupon.discount_inr}`);
    } catch (e) {
      setError(e.message || "Coupon check failed");
    }
  };

  const removeCoupon = () => { setApplied(null); setCouponCode(""); setCouponMsg(""); };

  const postCouponSubtotal = Math.max(0, subtotal - (applied?.discount_inr || 0));
  const coinDiscount = coinPreview
    ? Math.max(0, Math.min(coinsToApply, coinPreview.max_coins_by_cap || 0, coinPreview.balance || 0))
      * (coinPreview.value_per_coin_inr || 0)
    : 0;
  const dc = Math.max(0, Number(deliveryCharge) || 0);
  // v149.40 — A seller's order is its lines: no gateway minimum, no
  // coupon, no delivery charge. v149.63 — less the customer's coins.
  const sellerCoinOff = isSeller ? sellerCoinDiscount(sellerQuote, coinsToApply) : 0;
  const sellerCoinsUsed = sellerCoinOff > 0
    ? Math.min(Number(coinsToApply) || 0, Number(sellerQuote?.max_usable) || 0) : 0;
  const total = isSeller
    ? Math.round((subtotal - sellerCoinOff) * 100) / 100
    : Math.max(1, postCouponSubtotal - coinDiscount + dc);
  // A line whose selling price isn't a usable number can't be placed.
  const badPriceLine = isSeller ? cartList.find((i) => i.priceInvalid) : null;

  const creatorUserId = cartList[0]?.product?.creator_user_id || null;
  // v145.29 — Build a synthetic "product" so LoyaltyCoinPanel (which
  // expects a product with `creator_user_id`) can drive coin redemption
  // on the CART total. The panel handles preview/slider itself.
  const coinPanelProduct = useMemo(
    () => (creatorUserId && !isSeller ? { creator_user_id: creatorUserId } : null),
    [creatorUserId, isSeller],
  );

  // v149.40 — The seller's "Place order": checks what the form can check,
  // then hands over to `seller.place`, which calls the manual-order
  // endpoint (where everything is checked again).
  const submitSeller = async () => {
    if (cartList.length === 0) { setError("Add a product first."); return; }
    if (badPriceLine) { setError(`Enter a selling price for ${badPriceLine.product.title}.`); return; }
    const mail = email.trim();
    if (mail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) { setError("That email address doesn't look right."); return; }
    setStep("processing");
    try {
      const placed = await seller.place({
        name: name.trim(), email: mail, phone: phone.trim(), address: address.trim(),
        markDelivered: fulfilment === "now",
        applyCoins: sellerCoinsUsed,
      });
      setReceipt(placed);
      setStep("done");
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e2?.message || "Couldn't place the order"));
      setStep("form");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (isSeller) { await submitSeller(); return; }
    if (requirePhone && !phone.trim()) { setError("Phone number is required"); return; }
    if (requireAddress && !address.trim()) { setError("Delivery address is required"); return; }
    if (cartList.length === 0) { setError("Cart is empty"); return; }
    // v149.36 — COD needs somewhere to deliver and someone to call, the
    // same as the single-product COD checkout.
    if (paymentMethod === "cod") {
      if (!phone.trim()) { setError("Add a phone number so the seller can reach you."); return; }
      if (!address.trim()) { setError("Add a delivery address for Cash on Delivery."); return; }
    }
    setStep("processing");
    try {
      const orderRes = await fetch(`${API}/api/public/storefront/cart/order`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(typeof localStorage !== "undefined" && localStorage.getItem("creatoraiq_session_token")
            ? { Authorization: `Bearer ${localStorage.getItem("creatoraiq_session_token")}` }
            : {}),
        },
        body: JSON.stringify({
          creator_slug: slug, items,
          buyer_email: email.trim(),
          buyer_name: name.trim() || undefined,
          buyer_phone: phone.trim() || undefined,
          buyer_address: address.trim() || undefined,
          coupon_code: applied ? applied.code : undefined,
          apply_coins: Number(coinsToApply) || 0,
          delivery_charge_inr: dc,
          payment_method: paymentMethod,
        }),
      });
      // Parse defensively — proxies / gateways return plain-text HTML on 5xx,
      // which crashes .json() with an unhelpful "Unexpected token 'I'" error.
      const orderRaw = await orderRes.text();
      let orderData = {};
      try { orderData = orderRaw ? JSON.parse(orderRaw) : {}; } catch { /* keep {} */ }
      if (!orderRes.ok) {
        throw new Error(orderData?.detail || `Order failed (${orderRes.status}). Please try again in a moment.`);
      }
      // v149.36 — COD short-circuit. The backend has already written the
      // pending cart + one pending order per line; there is no gateway
      // step, so go straight to the confirmation.
      if (orderData.cod) {
        setCodOrder({
          order_id: orderData.order_id,
          order_ids: orderData.order_ids || [],
          amount_inr: orderData.cart?.amount_inr ?? total,
        });
        setReceipt(orderData.cart);
        setStep("done");
        try { window.dispatchEvent(new CustomEvent("coins:changed")); } catch { /* noop */ }
        return;
      }
      const { cart_id, order } = orderData;
      const finalize = async (payDetails) => {
        const vres = await fetch(`${API}/api/public/storefront/cart/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id, ...payDetails }),
        });
        const vRaw = await vres.text();
        let v = {};
        try { v = vRaw ? JSON.parse(vRaw) : {}; } catch { /* keep {} */ }
        if (!vres.ok) {
          throw new Error(v?.detail || `Verify failed (${vres.status}). Your payment may still succeed — please refresh in a minute.`);
        }
        try { window.dispatchEvent(new CustomEvent("coins:changed")); } catch { /* noop */ }
        setReceipt(v.cart || orderData.cart);
        setStep("done");
      };
      if (order.mock) {
        await finalize({
          razorpay_order_id: order.id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
          razorpay_signature: "mock",
        });
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay");
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: cleanText("Storefront cart", 60),
        description: cleanText(`${cartList.length} items`),
        order_id: order.id,
        prefill: cleanPrefill({ email, name, contact: phone }),
        theme: { color: "#FF6F61" },
        handler: async (rp) => {
          try {
            await finalize({
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
          } catch (e2) { setError(e2.message || "Payment failed to verify"); setStep("form"); }
        },
        modal: { ondismiss: () => setStep("form") },
      });
      rz.open();
    } catch (e2) {
      setError(e2.message || "Something went wrong");
      setStep("form");
    }
  };

  // ------------------------------------------------------------------
  // v145.29 — Order confirmation view (bill-style summary + copy /
  // download / close). Rendered in place of the form after successful
  // verify. Buyer explicitly acknowledges before we clear the cart.
  // ------------------------------------------------------------------
  const isCod = !!codOrder;
  const receiptText = useMemo(() => {
    if (!receipt) return "";
    if (isSeller) {
      // v149.40 — `receipt` is the manual-order response.
      const b = receipt.buyer || {};
      const inr = (v) => `₹${Number(v || 0).toFixed(2)}`;
      return [
        "LINKOLA · ORDER (PLACED BY THE SHOP)",
        "------------------------------",
        `Order No:  ${(receipt.order_ids || []).join(", ")}`,
        `Placed at: ${receipt.placed_at || new Date().toISOString()}`,
        `Priority:  ${String(receipt.priority || "normal").replace(/^./, (c) => c.toUpperCase())}`,
        `Customer:  ${b.name || "Walk-in"}${b.email ? ` <${b.email}>` : ""}`,
        b.phone ? `Phone:     ${b.phone}` : "",
        b.address ? `Address:   ${b.address}` : "",
        "------------------------------",
        "Items:",
        ...(receipt.items || []).map((i) => (
          `  ${i.units}x ${i.title} @ ${inr(i.unit_price_inr)}  ${inr(i.subtotal_inr)}`
          + (Number(i.unit_price_inr) !== Number(i.list_price_inr) ? ` (list ${inr(i.list_price_inr)})` : "")
        )),
        "------------------------------",
        receipt.coin_discount_inr ? `Subtotal:  ${inr(receipt.subtotal_inr)}` : "",
        receipt.coin_discount_inr
          ? `Coins:     -${inr(receipt.coin_discount_inr)} (${receipt.loyalty?.coins_applied} ${receipt.loyalty?.coin_name || "coins"})` : "",
        `TOTAL:     ${inr(receipt.total_inr)}`,
        receipt.loyalty?.coins_earned
          ? `Earned:    +${receipt.loyalty.coins_earned} ${receipt.loyalty.coin_name || "coins"}` : "",
        receipt.loyalty?.balance_after != null
          ? `Balance:   ${receipt.loyalty.balance_after} ${receipt.loyalty.coin_name || "coins"}` : "",
        "Payment:   Paid in person",
        `Hand-over: ${receipt.delivered ? "Handed over at the counter" : "To be delivered"}`,
      ].filter(Boolean).join("\n");
    }
    const lines = [
      isCod ? "LINKOLA · ORDER · PENDING CONFIRMATION" : "LINKOLA · ORDER RECEIPT",
      "------------------------------",
      codOrder?.order_id ? `Order No:  ${codOrder.order_id}` : "",
      `Cart ID:   ${receipt.cart_id}`,
      isCod
        ? `Placed at: ${new Date().toISOString()}`
        : `Paid at:   ${(receipt.paid_at || new Date().toISOString())}`,
      `Storefront: ${receipt.creator_slug || slug}`,
      `Buyer:     ${receipt.buyer_name || ""} <${receipt.buyer_email}>`,
      receipt.buyer_phone ? `Phone:     ${receipt.buyer_phone}` : "",
      receipt.buyer_address ? `Address:   ${receipt.buyer_address}` : "",
      "------------------------------",
      "Items:",
      ...(receipt.items || []).map((i) => `  ${i.units}x ${i.title}  ₹${Number(i.subtotal_inr || 0).toFixed(2)}`),
      "------------------------------",
      `Subtotal:  ₹${Number(receipt.subtotal_inr || 0).toFixed(2)}`,
      receipt.discount_inr ? `Coupon:    -₹${Number(receipt.discount_inr || 0).toFixed(2)}` : "",
      receipt.coin_discount_inr ? `Coins:     -₹${Number(receipt.coin_discount_inr || 0).toFixed(2)}` : "",
      receipt.delivery_charge_inr ? `Delivery:  ₹${Number(receipt.delivery_charge_inr || 0).toFixed(2)}` : "",
      `TOTAL:     ₹${Number(receipt.amount_inr || 0).toFixed(2)}`,
      isCod
        ? `Payment:   Cash on Delivery — pay ₹${Number(receipt.amount_inr || 0).toFixed(2)} at delivery`
        : `Payment:   ${receipt.razorpay_payment_id}`,
      "",
      isCod
        ? "The seller confirms this order before delivery."
        : "Show this to the store to confirm your order.",
    ].filter(Boolean);
    return lines.join("\n");
  }, [receipt, slug, isCod, codOrder, isSeller]);

  const copyReceipt = async () => {
    try { await navigator.clipboard.writeText(receiptText); toast.success("Receipt copied"); }
    catch { toast.error("Could not copy"); }
  };
  const downloadReceipt = () => {
    const blob = new Blob([receiptText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `linkola-receipt-${receipt?.cart_id || "order"}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const closeAndClear = () => { onSuccess({ cart: receipt }); };

  const modalNode = (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center overscroll-contain"
      onMouseDown={(e) => { if (step !== "processing" && e.target === e.currentTarget) onClose(); }}
      data-testid="cart-checkout-modal"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-md h-[92dvh] sm:h-auto sm:max-h-[92dvh] flex flex-col rounded-t-lg sm:rounded-none"
      >
        {step === "done" && receipt ? (
          // ============================================================
          // ORDER CONFIRMATION — bill-style receipt.
          // ============================================================
          // v149.71 — `flex-1 min-h-0`, not `h-full`: from `sm` up the panel's
          // height is its content's, capped at 92dvh. `h-full` of that is
          // "as tall as the content", so on a short screen (a phone on its
          // side) the receipt ran past the cap and took Done off the screen.
          // This keeps the body scrolling inside the panel and the footer in view.
          <div className="flex flex-col flex-1 min-h-0" data-testid="cart-checkout-done">
            <div className="flex items-center gap-2 text-mint p-4 border-b-2 border-ink shrink-0">
              <CheckCircle2 className="w-6 h-6" strokeWidth={2.5} />
              <h3 className="font-black text-lg text-ink">
                {isCod ? "Order placed — pending confirmation" : "Order placed"}
              </h3>
              <button type="button" onClick={closeAndClear} className="ml-auto p-1" data-testid="cart-checkout-done-close">
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {/* v149.36 — The order number, first thing, so the buyer can
                  quote it without digging through the bill below. */}
              {codOrder?.order_id && (
                <div className="border-2 border-ink bg-mint p-3 space-y-1" data-testid="cart-receipt-cod-banner">
                  <p className="text-[10px] font-black uppercase tracking-widest">Order number</p>
                  <p className="font-mono font-black text-sm break-all" data-testid="cart-receipt-order-id">
                    {codOrder.order_id}
                  </p>
                  <p className="text-[11px] leading-snug">
                    The seller has been notified and will confirm your COD order shortly.
                    Keep <b>₹{Number(receipt.amount_inr || 0).toFixed(2)}</b> ready at delivery / pickup —
                    payment is collected only after the seller confirms.
                  </p>
                </div>
              )}
              {isSeller && (
                <div className="border-2 border-ink bg-mint p-3 space-y-1 min-w-0" data-testid="cart-receipt-seller-banner">
                  <p className="text-[10px] font-black uppercase tracking-widest">
                    Order number{(receipt.order_ids || []).length > 1 ? "s" : ""}
                  </p>
                  <p className="font-mono font-black text-sm break-all" data-testid="cart-receipt-order-id">
                    {(receipt.order_ids || []).join(", ")}
                  </p>
                  <p className="text-[11px] leading-snug">
                    {receipt.delivered
                      ? "Handed over and paid in person. It's in Orders as delivered, and the stock has come off."
                      : "Paid in person, to be delivered. It's waiting in Orders — marking it delivered takes the stock off."}
                  </p>
                </div>
              )}
              {!isCod && !isSeller && (
                <div className="border-2 border-dashed border-ink bg-cream p-3 text-[11px] flex items-start gap-2" data-testid="cart-receipt-email-note">
                  <span className="font-bold text-mint">✉</span>
                  <span>A tax invoice + receipt has been emailed to <b>{receipt.buyer_email}</b>. Keep it for your records.</span>
                </div>
              )}
              <pre
                className="border-2 border-ink bg-cream p-3 text-[11px] font-mono whitespace-pre-wrap leading-snug"
                data-testid="cart-receipt-text"
              >
                {receiptText}
              </pre>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={copyReceipt}
                        className="border-2 border-ink bg-white px-3 py-2 text-xs font-bold shadow-brutalSm inline-flex items-center justify-center gap-1"
                        data-testid="cart-receipt-copy">
                  <Copy className="w-3 h-3" strokeWidth={3} /> Copy
                </button>
                <button type="button" onClick={downloadReceipt}
                        className="border-2 border-ink bg-white px-3 py-2 text-xs font-bold shadow-brutalSm inline-flex items-center justify-center gap-1"
                        data-testid="cart-receipt-download">
                  <Download className="w-3 h-3" strokeWidth={3} /> Download
                </button>
              </div>
            </div>
            <div className="p-4 border-t-2 border-ink bg-white shrink-0 space-y-2 min-w-0"
                 style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
              {/* v149.71 — The shop's own order: send the bill on WhatsApp,
                  or print it. Pinned with Done so neither is ever below
                  the fold. (A customer's checkout doesn't show these.) */}
              {isSeller && (receipt.order_ids || []).length > 0 && (
                <Suspense fallback={<div className="h-[42px]" aria-hidden="true" />}>
                  <BillActions
                    orderId={receipt.order_ids[0]}
                    buyerPhone={receipt.buyer?.phone}
                    testidPrefix="cart-receipt-bill"
                  />
                </Suspense>
              )}
              <button type="button" onClick={closeAndClear}
                      className="w-full border-2 border-ink bg-coral text-white px-3 py-2.5 font-black text-sm shadow-brutalSm"
                      data-testid="cart-receipt-close">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center gap-2 justify-between p-4 border-b-2 border-ink shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
                <h3 className="font-black text-lg">Checkout · {cartList.length} items</h3>
              </div>
              <button type="button" onClick={onClose} className="p-1" data-testid="cart-checkout-close"
                      aria-label={isSeller ? "Back to the order" : undefined}>
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              <div className="border-2 border-ink bg-cream p-2 space-y-1 text-sm">
                {isSeller ? (
                  // v149.40 — The brand can still change the order here:
                  // quantity, selling price, or take a line out.
                  cartList.length === 0 ? (
                    <p className="text-xs text-ink/70 py-1" data-testid="cart-checkout-seller-empty">
                      Nothing left in the order — go back and add a product.
                    </p>
                  ) : cartList.map((i, idx) => (
                    <SellerLine
                      key={i.product.product_id}
                      item={i}
                      index={idx}
                      disabled={step === "processing"}
                      onUnits={(u) => seller.onUnits(i.product.product_id, u)}
                      onPrice={(v) => seller.onPrice(i.product.product_id, v)}
                      onRemove={() => seller.onRemove(i.product.product_id)}
                    />
                  ))
                ) : cartList.map((i) => (
                  <div key={i.product.product_id} className="flex justify-between items-center gap-2">
                    <span className="truncate flex-1">
                      <span className="font-bold">{i.units}× </span>{i.product.title}
                    </span>
                    <span className="font-mono tabular-nums shrink-0">
                      ₹{(Number(i.product.price || 0) * i.units).toFixed(0)}
                    </span>
                  </div>
                ))}
                <div className="border-t-2 border-ink pt-1 mt-1 flex justify-between font-black">
                  <span>Subtotal</span><span className="tabular-nums">₹{subtotal.toFixed(0)}</span>
                </div>
                {applied && (
                  <div className="flex justify-between text-coral font-bold text-xs">
                    <span>Coupon {applied.code}</span><span className="tabular-nums">−₹{applied.discount_inr}</span>
                  </div>
                )}
                {coinDiscount > 0 && (
                  <div className="flex justify-between text-coral font-bold text-xs">
                    <span>Coins</span><span className="tabular-nums">−₹{coinDiscount.toFixed(0)}</span>
                  </div>
                )}
                {sellerCoinOff > 0 && (
                  <>
                    <div className="flex justify-between text-coral font-bold text-xs gap-2" data-testid="cart-checkout-seller-coins-line">
                      <span className="min-w-0 truncate">Coins ({sellerCoinsUsed.toLocaleString("en-IN")})</span>
                      <span className="tabular-nums shrink-0">−₹{sellerCoinOff.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-black text-sm" data-testid="cart-checkout-seller-total-line">
                      <span>To pay</span><span className="tabular-nums">₹{total.toFixed(2)}</span>
                    </div>
                  </>
                )}
                {dc > 0 && (
                  <div className="flex justify-between text-xs" data-testid="cart-checkout-delivery-line">
                    <span>Delivery charge</span><span className="tabular-nums">₹{dc.toFixed(0)}</span>
                  </div>
                )}
              </div>
              {isSeller ? (
                // v149.63 — The customer's phone first: it finds their coins.
                <div className="space-y-2 min-w-0" data-testid="cart-checkout-seller-customer">
                  <p className="text-[10px] font-bold uppercase tracking-wider pt-1">
                    Customer <span className="opacity-60">(all optional)</span>
                  </p>
                  <label className="block space-y-1 min-w-0">
                    <span className="text-[12px] font-black">Customer phone</span>
                    <PhoneInput
                      value={phone} onChange={setPhone}
                      placeholder="10-digit mobile number"
                      className="w-full"
                      data-testid="cart-checkout-phone"
                    />
                  </label>
                  <SellerCoinPanel
                    phone={phone}
                    email={email}
                    bill={subtotal}
                    coins={coinsToApply}
                    setCoins={setCoinsToApply}
                    onQuote={setSellerQuote}
                    disabled={step === "processing"}
                  />
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Customer name (Walk-in if left blank)" maxLength={80} autoComplete="off"
                    className="w-full min-w-0 border-2 border-ink px-3 py-2 text-sm"
                    data-testid="cart-checkout-name"
                  />
                  <input
                    type="email" autoComplete="off" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Customer email"
                    className="w-full min-w-0 border-2 border-ink px-3 py-2 text-sm"
                    data-testid="cart-checkout-email"
                  />
                  <textarea
                    value={address} onChange={(e) => setAddress(e.target.value)}
                    placeholder="Delivery address (optional)" rows={2} maxLength={500}
                    className="w-full min-w-0 border-2 border-ink px-3 py-2 text-sm"
                    data-testid="cart-checkout-address"
                  />
                </div>
              ) : (
                <>
                  <input
                    type="email" required autoComplete="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full border-2 border-ink px-3 py-2 text-sm"
                    data-testid="cart-checkout-email"
                  />
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name" maxLength={80}
                    className="w-full border-2 border-ink px-3 py-2 text-sm"
                    data-testid="cart-checkout-name"
                  />
                  <PhoneInput
                    value={phone} onChange={setPhone}
                    placeholder={requirePhone || paymentMethod === "cod" ? "10-digit mobile number *" : "10-digit mobile number (optional)"}
                    className="w-full"
                    data-testid="cart-checkout-phone"
                  />
                </>
              )}
              {/* v149.70 — Cash on Delivery always needs an address, so the
                  field is there whenever COD is the way this order is paid
                  (before, it only appeared when the seller required one —
                  a COD buyer was asked for an address with nowhere to type it). */}
              {!isSeller && (requireAddress || paymentMethod === "cod") && (
                <textarea
                  value={address} onChange={(e) => setAddress(e.target.value)}
                  placeholder="Shipping / delivery address *" rows={3} maxLength={500}
                  className="w-full border-2 border-ink px-3 py-2 text-sm"
                  data-testid="cart-checkout-address"
                  required
                />
              )}
              {isSeller && (
                <SellerOptions
                  priority={seller.priority}
                  onPriority={seller.onPriority}
                  fulfilment={fulfilment}
                  onFulfilment={setFulfilment}
                  disabled={step === "processing"}
                />
              )}
              {!isSeller && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4" strokeWidth={2.5} />
                  <p className="text-[11px] font-bold uppercase tracking-wider">Have a coupon?</p>
                </div>
                {applied ? (
                  <div className="border-2 border-ink bg-mint flex justify-between items-center px-3 py-2">
                    <span className="text-xs font-bold">{applied.code} · {applied.discount_pct}% off</span>
                    <button type="button" onClick={removeCoupon} className="text-xs underline" data-testid="cart-remove-coupon">
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text" value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                      placeholder="Enter code" maxLength={30}
                      className="flex-1 border-2 border-ink px-3 py-2 text-sm uppercase tracking-wider"
                      data-testid="cart-coupon-input"
                    />
                    <button type="button" onClick={applyCoupon}
                            className="border-2 border-ink bg-white px-3 py-2 text-xs font-bold shadow-brutalSm"
                            data-testid="cart-apply-coupon">
                      Apply
                    </button>
                  </div>
                )}
                {couponMsg && <p className="text-[11px] text-ink/60">{couponMsg}</p>}
              </div>
              )}
              {coinPanelProduct && (
                // v145.29 — Coin redemption on cart total. Uses the same
                // panel as single-product checkout; state lifted so we
                // can send `apply_coins` in the order body.
                <LoyaltyCoinPanel
                  product={coinPanelProduct}
                  subtotalAfterCoupon={postCouponSubtotal}
                  coinsToApply={coinsToApply}
                  setCoinsToApply={setCoinsToApply}
                  coinPreview={coinPreview}
                  setCoinPreview={setCoinPreview}
                />
              )}
              {error && <p className="text-xs text-coral font-bold" data-testid="cart-checkout-error">{error}</p>}
            </div>
            <div className="p-4 border-t-2 border-ink bg-white shrink-0 space-y-1"
                 style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
              {isSeller ? (
                <>
                  <button
                    type="submit"
                    disabled={step === "processing" || cartList.length === 0 || !!badPriceLine}
                    className="w-full border-2 border-ink bg-coral text-white px-3 py-2.5 font-black text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="cart-checkout-place-order"
                  >
                    {step === "processing" && <Loader2 className="w-4 h-4 animate-spin" />}
                    Place order · ₹{total.toFixed(2)}
                  </button>
                  <p className="text-[10px] text-ink/60 text-center">
                    Paid in person · no payment gateway
                  </p>
                </>
              ) : (
              <>
              <CartCoinEarnStrip slug={slug} amountInr={total} />
              {/* v149.36 — Payment method. Only rendered when the seller
                  actually takes COD (plan + toggle, decided server-side),
                  so a shop without it sees exactly what it saw before. */}
              {codOnly && (
                <div className="border-2 border-ink bg-cream p-2" data-testid="cart-checkout-payment-method">
                  <p className="text-[11px] font-bold" data-testid="cart-checkout-cod-only">
                    Cash on Delivery
                    <span className="block font-normal text-ink/70 mt-0.5">
                      This seller takes payment in cash at delivery / pickup.
                    </span>
                  </p>
                </div>
              )}
              {codEnabled && !codOnly && (
                <div className="grid grid-cols-2 gap-2" data-testid="cart-checkout-payment-method">
                  {[["razorpay", "Pay Now"], ["cod", "Cash on Delivery"]].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPaymentMethod(key)}
                      data-testid={`cart-checkout-method-${key}`}
                      className={`border-2 border-ink px-2 py-2 text-xs font-bold ${
                        paymentMethod === key ? "bg-ink text-white" : "bg-white"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="submit" disabled={step === "processing"}
                className="w-full border-2 border-ink bg-coral text-white px-3 py-2.5 font-black text-sm shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="cart-checkout-pay"
              >
                {step === "processing" && <Loader2 className="w-4 h-4 animate-spin" />}
                {paymentMethod === "cod"
                  ? `Place order · Pay ₹${total.toFixed(2)} at delivery`
                  : `Pay ₹${total.toFixed(2)}`}
              </button>
              <p className="text-[10px] text-ink/60 text-center">
                {paymentMethod === "cod"
                  ? "Cash on Delivery · Confirmed by seller"
                  : "Powered by Razorpay · Secure checkout"}
              </p>
              </>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );

  // v145.31 — Portal to <body> so parent transforms / stacking contexts
  // (Leaflet maps, block containers) don't break `position: fixed` or
  // clip the modal. Also puts us above the AppShell's bottom nav.
  if (typeof document === "undefined") return null;
  return createPortal(modalNode, document.body);
}


/**
 * v149.40 — One line of the brand's New order at checkout: quantity,
 * selling price (the catalogue price is shown whenever it's changed),
 * and remove. `item.priceInput` is the text being typed; the parent
 * parses it into `item.product.price`.
 */
function SellerLine({ item, index, disabled, onUnits, onPrice, onRemove }) {
  const { product, units, priceInput, listPrice, priceInvalid } = item;
  // The quantity box keeps its own text so it can be cleared and retyped;
  // only a real number ≥ 1 reaches the order.
  const [qtyText, setQtyText] = useState(String(units));
  useEffect(() => { setQtyText(String(units)); }, [units]);
  const changed = !priceInvalid && Number(product.price) !== Number(listPrice);
  return (
    <div className="border-b border-ink/15 last:border-b-0 py-1.5 space-y-1.5 min-w-0"
         data-testid={`cart-checkout-line-${index}`}>
      <div className="flex items-start justify-between gap-2 min-w-0">
        <p className="font-bold text-sm break-words min-w-0 flex-1" data-testid={`cart-checkout-line-title-${index}`}>
          {product.title}
        </p>
        <span className="font-mono tabular-nums shrink-0 font-bold" data-testid={`cart-checkout-line-total-${index}`}>
          ₹{(Number(product.price || 0) * units).toFixed(2)}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <div className="inline-flex items-stretch border-2 border-ink bg-white">
          <button type="button" onClick={() => onUnits(units - 1)} disabled={disabled || units <= 1}
                  aria-label="One less" data-testid={`cart-checkout-line-minus-${index}`}
                  className="px-2 py-1 disabled:opacity-40">
            <Minus className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
          <input
            type="number" inputMode="numeric" min={1} max={10000} value={qtyText} disabled={disabled}
            onChange={(e) => {
              const text = e.target.value.replace(/[^0-9]/g, "");
              setQtyText(text);
              const n = Number(text);
              if (n >= 1) onUnits(Math.min(10000, n));
            }}
            onBlur={() => setQtyText(String(units))}
            aria-label={`Quantity for ${product.title}`}
            data-testid={`cart-checkout-line-qty-${index}`}
            className="w-12 min-w-0 border-x-2 border-ink text-center font-black text-sm"
          />
          <button type="button" onClick={() => onUnits(Math.min(10000, units + 1))} disabled={disabled}
                  aria-label="One more" data-testid={`cart-checkout-line-plus-${index}`}
                  className="px-2 py-1 bg-mint">
            <Plus className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </div>
        <label className="inline-flex items-stretch border-2 border-ink bg-white min-w-0">
          <span className="px-1.5 text-xs font-bold border-r-2 border-ink bg-cream flex items-center">₹</span>
          <input
            type="text" inputMode="decimal" value={priceInput} disabled={disabled}
            onChange={(e) => onPrice(e.target.value)}
            aria-label={`Selling price for ${product.title}`}
            data-testid={`cart-checkout-line-price-${index}`}
            className="w-20 min-w-0 px-1.5 py-1 text-sm font-bold tabular-nums"
          />
        </label>
        <button type="button" onClick={onRemove} disabled={disabled}
                aria-label={`Remove ${product.title}`} data-testid={`cart-checkout-line-remove-${index}`}
                className="ml-auto border-2 border-ink bg-white p-1.5">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {priceInvalid ? (
        <p className="text-[11px] text-coral font-bold">Enter a selling price.</p>
      ) : changed ? (
        <p className="text-[11px] text-ink/60" data-testid={`cart-checkout-line-list-${index}`}>
          List price ₹{Number(listPrice).toFixed(2)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * v149.40 — The brand's order settings at checkout: priority, and
 * whether the goods leave the counter now or are delivered later.
 */
function SellerOptions({ priority, onPriority, fulfilment, onFulfilment, disabled }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-[11px] font-bold uppercase tracking-wider">Priority</p>
        <PriorityPicker value={priority} onChange={onPriority} disabled={disabled}
                        testIdPrefix="cart-checkout-priority" />
      </div>
      <div className="space-y-1">
        <p className="text-[11px] font-bold uppercase tracking-wider">Hand-over</p>
        <div className="grid grid-cols-2 gap-2" data-testid="cart-checkout-fulfilment">
          {[["now", "Handed over now"], ["later", "Deliver later"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => onFulfilment(key)}
              disabled={disabled}
              aria-pressed={fulfilment === key}
              data-testid={`cart-checkout-fulfilment-${key}`}
              className={`border-2 border-ink px-2 py-2 text-xs font-bold ${
                fulfilment === key ? "bg-ink text-white" : "bg-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-ink/60 leading-snug">
          {fulfilment === "now"
            ? "Marked delivered now, and the stock comes off."
            : "Stays open in Orders until you mark it delivered — that's when the stock comes off."}
        </p>
      </div>
    </div>
  );
}


/**
 * v146.42 — Small "Earn N coins" strip that sits above the Pay CTA in
 * the cart. Silently renders nothing when the creator's loyalty
 * program is disabled OR the earn-per-100 rule is zero, so the layout
 * doesn't wobble when a shop hasn't opted in.
 */
function CartCoinEarnStrip({ slug, amountInr }) {
  const loyalty = useCreatorLoyalty(slug);
  const coins = loyalty.coinsForAmount(amountInr);
  if (coins <= 0) return null;
  return (
    <CoinEarnChip
      coins={coins}
      currentBalance={loyalty.balance}
      coinName={loyalty.coin_name}
      size="md"
      testId="cart-checkout-coin-earn"
    />
  );
}
